import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import Layout from "../components/layout/LegalLayout.jsx";
import config from "../config.js";
import { useSelector } from "react-redux";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const ActiveCases = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;
  const selectedCompanyId = useSelector((state) => state.kyc.selectedCompanyId);

  const fetchCases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/v1/legalcases/${selectedCompanyId}`);
      setCases(response.data.data || []);
    } catch (error) {
      setError("Error fetching cases: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      fetchCases();
    }
  }, [selectedCompanyId]);

  const chartData = {
    labels: ['In Progress', 'Pending Review', 'Waiting for Client'],
    datasets: [{
      label: 'Number of Cases',
      data: [
        cases.filter(c => c.status === 'In Progress').length,
        cases.filter(c => c.status === 'Pending Review').length,
        cases.filter(c => c.status === 'Waiting for Client').length
      ],
      backgroundColor: [
        'rgba(25, 118, 210, 0.8)',  
        'rgba(255, 152, 0, 0.8)',   
        'rgba(156, 39, 176, 0.8)'   
      ],
      borderColor: [
        'rgba(25, 118, 210, 1)',
        'rgba(255, 152, 0, 1)',
        'rgba(156, 39, 176, 1)'
      ],
      borderWidth: 1
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Case Status Distribution'
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'In Progress':
        return 'primary';
      case 'Pending Review':
        return 'warning';
      case 'Waiting for Client':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'error';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Active Cases Dashboard
        </Typography>

        <Grid container spacing={3}>
          {/* Status Distribution Chart */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Case Status Distribution
                </Typography>
                <Box sx={{ height: 300, position: 'relative' }}>
                  <Bar data={chartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Case Statistics */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Case Overview
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Total Cases</Typography>
                    <Typography variant="h5">{cases.length}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Active</Typography>
                    <Typography variant="h5" color="primary">
                      {cases.filter(c => c.status === 'In Progress').length}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="subtitle1">Pending</Typography>
                    <Typography variant="h5" color="warning.main">
                      {cases.filter(c => c.status === 'Pending Review').length}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Active Cases Table */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Active Cases
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Case ID</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cases.map((caseItem) => (
                        <TableRow key={caseItem._id}>
                          <TableCell>{caseItem.id}</TableCell>
                          <TableCell>{caseItem.client}</TableCell>
                          <TableCell>{caseItem.type}</TableCell>
                          <TableCell>
                            <Chip
                              label={caseItem.status}
                              color={getStatusColor(caseItem.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={caseItem.priority}
                              color={getPriorityColor(caseItem.priority)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{caseItem.assignedTo}</TableCell>
                          <TableCell>{formatDate(caseItem.lastUpdated)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error Snackbar */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => setError(null)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default ActiveCases;
